import { useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { usePendingSubmissions, PendingSubmission } from "@/hooks/usePendingReviews";
import { useStorageUrl } from "@/hooks/useStorageUrl";
import { getStorageUrl } from "@/lib/storage";
import { formatDistanceToNow } from "date-fns";
import { ClipboardCheck, Clock, GraduationCap, ArrowRight, Inbox, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

function useInstructorCourseCount() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["instructor-course-count", user?.id],
    queryFn: async () => {
      if (!user) return { isAdmin: false, courseCount: 0 };
      
      // Check if admin
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
      
      if (adminRole) {
        const { count } = await supabase
          .from("courses")
          .select("id", { count: "exact", head: true });
        return { isAdmin: true, courseCount: count || 0 };
      }
      
      // Count assigned courses
      const { count } = await supabase
        .from("course_instructors")
        .select("id", { count: "exact", head: true })
        .eq("instructor_id", user.id)
        .eq("can_grade", true);
      
      return { isAdmin: false, courseCount: count || 0 };
    },
    enabled: !!user,
  });
}

export default function ReviewQueue() {
  const navigate = useNavigate();
  const { data: submissions, isLoading } = usePendingSubmissions();
  const { data: instructorInfo, isLoading: isLoadingInstructor } = useInstructorCourseCount();

  if (isLoading || isLoadingInstructor) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Authorization for this route is enforced by <RequireRole anyOf={["instructor","admin"]}>
  // in App.tsx. Previously the ENTIRE gate was `instructorInfo?.courseCount === 0`:
  // when logged out both queries were disabled, so isLoading was false and
  // instructorInfo was undefined — and `undefined === 0` is false, so the full
  // queue rendered. Treat an unresolved result as "not ready" rather than
  // falling through.
  if (!instructorInfo) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
        </div>
      </DashboardLayout>
    );
  }

  // Check if user has any courses to grade
  if (instructorInfo.courseCount === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ClipboardCheck className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Review Queue</h1>
              <p className="text-muted-foreground">No courses assigned</p>
            </div>
          </div>

          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-medium mb-2">No Courses Assigned</h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                You are not currently assigned as an instructor for any courses. 
                Contact an administrator to be assigned to courses for grading.
              </p>
              <Button variant="outline" onClick={() => navigate("/dashboard")}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Group submissions by course
  const submissionsByCourse = (submissions || []).reduce((acc, sub) => {
    const courseId = sub.assignment.module.course.id;
    if (!acc[courseId]) {
      acc[courseId] = {
        course: sub.assignment.module.course,
        submissions: [],
      };
    }
    acc[courseId].submissions.push(sub);
    return acc;
  }, {} as Record<string, { course: { id: string; title: string; thumbnail_url: string | null }; submissions: PendingSubmission[] }>);

  const courseGroups = Object.values(submissionsByCourse);
  const totalPending = submissions?.length || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ClipboardCheck className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Review Queue</h1>
            <p className="text-muted-foreground">
              {totalPending > 0 
                ? `${totalPending} submission${totalPending !== 1 ? 's' : ''} awaiting review`
                : "All caught up! No pending submissions."}
              {instructorInfo?.isAdmin && (
                <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                  Admin View
                </span>
              )}
            </p>
          </div>
        </div>

        {totalPending === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Inbox className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium mb-2">No pending reviews</h3>
              <p className="text-muted-foreground mb-4">
                Great job! All student submissions have been reviewed.
              </p>
              <Button variant="outline" onClick={() => navigate("/instructor/courses")}>
                Go to Courses
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {courseGroups.map(({ course, submissions: courseSubmissions }) => (
              <Card key={course.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-4">
                    {/* Course Thumbnail */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-muted">
                      {course.thumbnail_url ? (
                        <img
                          src={getStorageUrl("course-content", course.thumbnail_url) || ""}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                          <GraduationCap className="h-5 w-5 text-primary/60" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{course.title}</CardTitle>
                      <CardDescription>
                        {courseSubmissions.length} pending submission{courseSubmissions.length !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {courseSubmissions.map((submission) => (
                    <SubmissionRow 
                      key={submission.id} 
                      submission={submission} 
                    />
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function SubmissionRow({ submission }: { submission: PendingSubmission }) {
  const avatarUrl = useStorageUrl("avatars", submission.profiles?.avatar_url);

  return (
    <Link
      to={`/instructor/submissions/${submission.id}`}
      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9">
          <AvatarImage src={avatarUrl || undefined} />
          <AvatarFallback className="bg-primary text-primary-foreground">
            {submission.profiles?.full_name?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">
            {submission.profiles?.full_name || "Unknown Student"}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {submission.assignment.title} • {submission.assignment.module.title}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right hidden sm:block">
          <Badge variant="outline" className="gap-1 text-blue-600 border-blue-200">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
          <p className="text-xs text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(submission.submitted_at), { addSuffix: true })}
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </Link>
  );
}
